import {Box, Button, Divider, Stack, useTheme} from '@mui/material';
import React from 'react';

import {RefundStatusEnum} from 'domain/usecases';
import {DownloadDocument, StyledCard, TagChip} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {FeaturesEnum, OperationStateTypesEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {cardStyleByStatus} from '../../constants/cardByStatus';
import {useGetRefundPdf} from '../../hooks';
import {formatRefundDate} from '../../utils';
import {RequestErrorModal} from '../RequestErrorModal';
import {RefundCardProps} from './types';
import {RefundCardTitle} from '../RefundCardTitle';

const {seeRequest, seeMore} = translations['pt-br'].commons;
const {paymentDateTitle, expectedPaymentDateTitle, requestedAt, originProtocol, refundStatement} =
	translations['pt-br'].privateFlow.consultRefund;

export const RefundCard = ({data, onViewMoreClick}: RefundCardProps) => {
	const {situationCode, estimatedPaymentDate, paymentDate, protocol, startDate} = data;
	const theme = useTheme();
	const {
		downloadPdf,
		CHECK_REQUEST_IS_LOADING,
		DOWNLOAD_PDF_IS_LOADING,
		CHECK_REQUEST_IS_ERROR,
		DOWNLOAD_PDF_IS_ERROR,
		setCheckRequestOperationState,
		setDownloadPdfOperationState,
		checkRequest,
	} = useGetRefundPdf();
	const startDateFormatted = formatRefundDate(startDate);
	const expectedPaymentDateFormatted = formatRefundDate(estimatedPaymentDate);
	const paymentDateFormatted = formatRefundDate(paymentDate);

	const {footer, headerIcon, borderColor, iconColor} = cardStyleByStatus(theme, situationCode);

	const showPaymentDate = [RefundStatusEnum.CONCLUDED_8].includes(situationCode);

	const showExpectedPaymentDate = [RefundStatusEnum.APPROVED_7].includes(situationCode);

	const showDownloadDocument =
		[RefundStatusEnum.APPROVED_7].includes(situationCode) ||
		[RefundStatusEnum.CONCLUDED_8].includes(situationCode) ||
		[RefundStatusEnum.DENIED_5].includes(situationCode);

	async function handleDownloadPDF() {
		await downloadPdf(protocol);
	}

	const handleCheckRequest = async () => {
		await checkRequest(protocol);
	};

	const isShowDownloadDocument = showDownloadDocument ? '6px' : 0;

	const handleViewMore = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		onViewMoreClick(data);
	};

	const {isEnabled: isRN623Enabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_623_REEMBOLSO);

	return (
		<StyledCard.Container
			$borderTop
			$borderColor={borderColor}
			sx={{height: '100%', justifyContent: 'flex-start'}}>
			<StyledCard.Header
				headerProps={{gap: 1}}
				customIcon={<Icon name={headerIcon.customIconName} color={iconColor} />}
				titleColor={theme.palette.primary.dark}
				title={<RefundCardTitle highlightedText={protocol} />}
				gap={0.5}>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<TagChip label={footer.chipText} color={footer.chipVariant} />
					{isRN623Enabled ? (
						<Button
							variant="text"
							onClick={handleViewMore}
							data-tag-id={RefundTaggingEnum.CLICK_VER_MAIS_CONSULTAR_REEMBOLSO}
							endIcon={
								<Icon
									name="chevron-right-outlined"
									color={theme.palette.primary.main}
									size={20}
								/>
							}>
							{seeMore}
						</Button>
					) : null}
				</Box>
			</StyledCard.Header>
			<Divider />
			<StyledCard.Content
				sx={{
					pt: 1,
				}}
				list={[
					{
						primaryText: originProtocol,
						iconName: 'content-paste-filled',
						secondaryText: data?.requestSource || ' - ',
						primaryTextColor: 'text.secondary',
						primaryTextFontSize: '0.875rem',
						secondaryTextFontSize: '1rem',
					},
				]}
			/>
			<StyledCard.Content
				sx={{
					marginBottom: 'auto',
					flexDirection: 'column',
					pb: 1,
				}}
				list={[
					{
						primaryText: requestedAt,
						iconName: 'event-filled',
						secondaryText: startDateFormatted,
						primaryTextColor: 'text.secondary',
						primaryTextFontSize: '0.875rem',
						secondaryTextFontSize: '1rem',
					},
					{
						primaryText: expectedPaymentDateTitle,
						iconName: 'monetization-on-filled',
						secondaryText: expectedPaymentDateFormatted,
						primaryTextColor: 'text.secondary',
						primaryTextFontSize: '0.875rem',
						secondaryTextFontSize: '1rem',
						hide: !showExpectedPaymentDate,
					},
					{
						primaryText: paymentDateTitle,
						iconName: 'monetization-on-filled',
						secondaryText: paymentDateFormatted,
						primaryTextColor: 'text.secondary',
						primaryTextFontSize: '0.875rem',
						secondaryTextFontSize: '1rem',
						hide: !showPaymentDate,
					},
				]}
			/>
			{showDownloadDocument ? (
				<DownloadDocument
					dataTagId={RefundTaggingEnum.CLICK_DOWNLOAD_DEMONSTRATIVO_REEMBOLSO}
					label={refundStatement}
					handleAction={handleCheckRequest}
					isLoading={CHECK_REQUEST_IS_LOADING}
				/>
			) : null}
			{showDownloadDocument ? null : (
				<Stack
					direction="row"
					justifyContent="flex-end"
					alignItems="center"
					py={isShowDownloadDocument}>
					<Button
						endIcon={
							DOWNLOAD_PDF_IS_LOADING ? (
								<Loading size={24} fullWidth={false} color="inherit" />
							) : (
								<Icon name="picture-as-pdf-filled" />
							)
						}
						variant="text"
						disabled={DOWNLOAD_PDF_IS_LOADING}
						onClick={handleDownloadPDF}
						data-tag-id={RefundTaggingEnum.CLICK_VER_SOLICITACAO_CONSULTA_REEMBOLSO}>
						{seeRequest}
					</Button>
				</Stack>
			)}

			<RequestErrorModal
				open={DOWNLOAD_PDF_IS_ERROR}
				setOpen={() => setDownloadPdfOperationState(OperationStateTypesEnum.NONE)}
			/>
			<RequestErrorModal
				open={CHECK_REQUEST_IS_ERROR}
				setOpen={() => setCheckRequestOperationState(OperationStateTypesEnum.NONE)}
			/>
		</StyledCard.Container>
	);
};
